import { Injectable } from '@angular/core';
import { EmployeeSummaryEntity } from '../../../meta-data/app-meta-data.service';
import { EmployeeReviewEntity, EmployeeReviewQuestionSetupEntity, EmployeeReviewQuestionEntity, EmployeeReviewCategoryEntity, EmployeeReviewAnswerEntity } from '../meta-data/performancemanagement-meta-data.service';
import { EmployeeReviewQuestionData, EmployeeReviewFlatAnswerValues } from '../models/employee-review-question-data.model';
import { EmployeeReviewCategories } from '../meta-data/constants/employee-review-categories.enum';
import { EmployeeReviewQuestionType } from '../meta-data/constants/employee-review-question-type.enum';

@Injectable({ providedIn: 'root' })
export class PerformanceManagementReviewService {

    /** Process the empoyee reviews and creates a model to display in the UI
     * 
     * This process creates a single model of custom/skills questions that combine both the employee and the supervisors answers.  It also performs analysis on the soft/hard skills to create a scatter plot graph
    */
    processEmployeeReviewAnalysis(employeeReviews: EmployeeReviewEntity[], questionSetup: EmployeeReviewQuestionSetupEntity[], questionData: EmployeeReviewQuestionData): EmployeeReviewAnalysis {
        // Determine if Revenue and Bidding questions should be included
        const revenueBiddingOptions = this.determineRevenueBiddingOptionsByReviews(employeeReviews);

        // Seperate out the employee & supervisor reviews
        const employeeReview = employeeReviews.find(r => r.IsSupervisorReview == false);
        const supervisorReview = employeeReviews.find(r => r.IsSupervisorReview == true);

        // Combine supervisor/employee answers for skill and custom questions (Builds a model easily displayed in the UI)
        const analysis: Partial<EmployeeReviewAnalysis> = {
            customQuestions: this.buildCustomQuestionsSummary(employeeReview, supervisorReview, questionData, questionSetup),
            skillQuestions: this.buildSkillQuestions(employeeReview, supervisorReview, questionData, revenueBiddingOptions),
        };

        // Calcultes the hard/soft skill stats for the chart
        this.calculateSkillStats(analysis);

        return analysis as EmployeeReviewAnalysis;
    }

    
    /**
     * Builds a list of all relavent customers questions combining employee & supervisor answers.  (Uses the global/department/team setup to determine relavent questions)
     */
    private buildCustomQuestionsSummary(employeeReview: EmployeeReviewEntity, supervisorReview: EmployeeReviewEntity, questionData: EmployeeReviewQuestionData, questionSetup: EmployeeReviewQuestionSetupEntity[]) {
        const customQuestions: QuestionSummary[] = [];

        for (const category of questionData.categories) {
            for (const question of category.EmployeeReviewQuestion) {
                if (question.EmployeeReviewQuestionTypeId == EmployeeReviewQuestionType.custom) {

                    // Determine if this question should be displayed
                    const customQuestion = questionSetup.find(qs => qs.EmployeeReviewQuestionSetupDetail?.find(qsd => qsd.EmployeeReviewQuestionId == question.EmployeeReviewQuestionId));

                    if (customQuestion) {
                        customQuestions.push(this.createQuestionSummary(question, employeeReview, supervisorReview));
                    }
                }
            }
        }

        return customQuestions;
    }

    /**
     * Builds a list of all relavent skill questions combining employee & supervisor answers (Uses the Revenue/Billing options to determine if these categories should be included)
     */
    private buildSkillQuestions(employeeReview: EmployeeReviewEntity, supervisorReview: EmployeeReviewEntity, questionData: EmployeeReviewQuestionData, revenueBiddingOptions: RevenueBillingOptions) {
        const skillQuestions: QuestionSummary[] = [];

        for (const category of questionData.categories) {
            if (!this.includeCategory(category, revenueBiddingOptions)) {
                continue;
            }

            for (const question of category.EmployeeReviewQuestion) {
                if (question.EmployeeReviewQuestionTypeId == EmployeeReviewQuestionType.skill && question.EmployeeReviewQuestionSkillId) {
                    skillQuestions.push(this.createSkillQuestionSummary(question, employeeReview, supervisorReview, questionData.flatAnswerValues));
                }
            }
        }

        return skillQuestions;
    }

  

       /**
     * Creates a question summary to combine employee & supervisor answers for a given question
     * @returns 
     */
        private createQuestionSummary(question: EmployeeReviewQuestionEntity, employeeReview: EmployeeReviewEntity, supervisorReview: EmployeeReviewEntity): QuestionSummary {
            const employeeAnswer = employeeReview.EmployeeReviewAnswer.find(ra => ra.EmployeeReviewQuestionId == question.EmployeeReviewQuestionId);
            const supervisorAnswer = supervisorReview.EmployeeReviewAnswer.find(ra => ra.EmployeeReviewQuestionId == question.EmployeeReviewQuestionId);
    
            const questionSummary: QuestionSummary = {

                question: question,
                employeeAnswer: employeeAnswer,
                supervisorAnswer: supervisorAnswer,
                
                employeeDisplay: employeeAnswer?.AnswerValueText,
                supervisorDisplay: supervisorAnswer?.AnswerValueText,
            };
    
            
            
            return questionSummary;
        }

    /**
     * Creates a question summary to combine employee & supervisor answers for a given question
     * @returns 
     */
    private createSkillQuestionSummary(question: EmployeeReviewQuestionEntity, employeeReview: EmployeeReviewEntity, supervisorReview: EmployeeReviewEntity, flatAnswerValues: EmployeeReviewFlatAnswerValues): QuestionSummary {
        const employeeAnswer = employeeReview.EmployeeReviewAnswer.find(ra => ra.EmployeeReviewQuestionId == question.EmployeeReviewQuestionId);
        const supervisorAnswer = supervisorReview.EmployeeReviewAnswer.find(ra => ra.EmployeeReviewQuestionId == question.EmployeeReviewQuestionId);

        const questionSummary: QuestionSummary = {
            question: question,
            employeeAnswer: employeeAnswer,
            supervisorAnswer: supervisorAnswer,
            employeeScore: flatAnswerValues[employeeAnswer?.EmployeeReviewAnswerValueId]?.Weight,
            employeeDisplay: flatAnswerValues[employeeAnswer?.EmployeeReviewAnswerValueId]?.AnswerDisplay,
            supervisorScore: flatAnswerValues[supervisorAnswer?.EmployeeReviewAnswerValueId]?.Weight,
            supervisorDisplay: flatAnswerValues[supervisorAnswer?.EmployeeReviewAnswerValueId]?.AnswerDisplay,
        };

        return questionSummary;
    }

    /**
     * Calculate all hard/soft skill stats for use with the scatter graph
     * @param analysis 
     */
    private calculateSkillStats(analysis: Partial<EmployeeReviewAnalysis>) {
        const employeeRollupTotals: ScoreRollupTotals = { hardSkillCount:0, hardSkillTotal:0, softSkillCount : 0, softSkillTotal: 0, allSkillScores: [] };
        const supervisorRollupTotals: ScoreRollupTotals = { hardSkillCount:0, hardSkillTotal:0, softSkillCount : 0, softSkillTotal: 0, allSkillScores: [] };

        // Create totals for soft/hard skills for the employee & supervisor
        for(const questionSummary of analysis.skillQuestions) {

            this.updateRollupScores(employeeRollupTotals, questionSummary, questionSummary.employeeScore);
            this.updateRollupScores(supervisorRollupTotals, questionSummary, questionSummary.supervisorScore);
        }

        // Computes all stats for the employee
        analysis.employeeSkillAverage = {
            hardSkillAverage : this.computeAverage(employeeRollupTotals.hardSkillCount, employeeRollupTotals.hardSkillTotal),
            softSkillAverage : this.computeAverage(employeeRollupTotals.softSkillCount, employeeRollupTotals.softSkillTotal),
            bubbleSize: this.computeBubbleSize(employeeRollupTotals),
            rollupTotals : employeeRollupTotals
        };

        // Computes all stats for the supervisor
        analysis.supervisorSkillAverage = {
            hardSkillAverage : this.computeAverage(supervisorRollupTotals.hardSkillCount, supervisorRollupTotals.hardSkillTotal),
            softSkillAverage : this.computeAverage(supervisorRollupTotals.softSkillCount, supervisorRollupTotals.softSkillTotal),
            bubbleSize: this.computeBubbleSize(supervisorRollupTotals),
            rollupTotals : supervisorRollupTotals
        };
    }

    /**
     * Update the soft/hard skills fora question summary
     * @param scoreRollupTotals 
     * @param questionSummary 
     * @param value 
     * @returns 
     */
    private updateRollupScores(scoreRollupTotals: ScoreRollupTotals, questionSummary: QuestionSummary, score: number) {
        if (!score) {
            return;
        }

        scoreRollupTotals.allSkillScores.push(score);

        if (questionSummary.question.EmployeeReviewQuestionSkill.IsSoftSkill) {
        
            scoreRollupTotals.softSkillCount += 1;
            scoreRollupTotals.softSkillTotal += score;
        } else {
            scoreRollupTotals.hardSkillCount += 1;
            scoreRollupTotals.hardSkillTotal += score;
        }
    }

     /**
     * Calculate the Bubble Size - 3rd time is the charm
     * 
     * WI 54174 - Bubble Size = (standard deviation of all applicable scores) 
     *     Just use the standard deviation - no need to adjust it based on average values
     * @param rollupTotals 
     */
      private computeBubbleSize(rollupTotals: ScoreRollupTotals): number {
        // Calc the overal totals
        rollupTotals.overallCount = rollupTotals.hardSkillCount + rollupTotals.softSkillCount;
        rollupTotals.overallTotal = rollupTotals.hardSkillTotal + rollupTotals.softSkillTotal;

        // Calc the average for the totals
        rollupTotals.overallAverage = this.computeAverage(rollupTotals.overallCount, rollupTotals.overallTotal);
                 
        // Calc the standard diviation of all values 
        rollupTotals.stdDeviation = this.computeStdDev(rollupTotals.allSkillScores, rollupTotals.overallAverage);

        // Calc the final bubble size
        rollupTotals.bubbleSize = rollupTotals.stdDeviation;

        return rollupTotals.bubbleSize;
    }

    /**
     * Calculate the Bubble Size
     * 
     * WI 54174 - Bubble Size = (standard deviation of all applicable scores) / ((sum of all applicable scores) / (count of all applicable scores))
     *     Swapped the StdDev is be first
     * @param rollupTotals 
     */
    private computeBubbleSizeTake2(rollupTotals: ScoreRollupTotals): number {
        // Calc the overal totals
        rollupTotals.overallCount = rollupTotals.hardSkillCount + rollupTotals.softSkillCount;
        rollupTotals.overallTotal = rollupTotals.hardSkillTotal + rollupTotals.softSkillTotal;

        // Calc the average for the totals
        rollupTotals.overallAverage = this.computeAverage(rollupTotals.overallCount, rollupTotals.overallTotal);
                 
        // Calc the standard diviation of all values 
        rollupTotals.stdDeviation = this.computeStdDev(rollupTotals.allSkillScores, rollupTotals.overallAverage);

        // Calc the final bubble size
        rollupTotals.bubbleSize = rollupTotals.stdDeviation / rollupTotals.overallAverage;

        return rollupTotals.bubbleSize;
    }

       /**
     * Calculate the Bubble Size
     * 
     * WI 52607 - Bubble Size = ((sum of all applicable scores)/(count of all applicable scores)) / (standard deviation of all applicable scores)
     * @param rollupTotals 
     */
        private computeBubbleSizeTake1(rollupTotals: ScoreRollupTotals): number {
            // Calc the overal totals
            rollupTotals.overallCount = rollupTotals.hardSkillCount + rollupTotals.softSkillCount;
            rollupTotals.overallTotal = rollupTotals.hardSkillTotal + rollupTotals.softSkillTotal;
    
            // Calc the average for the totals
            rollupTotals.overallAverage = this.computeAverage(rollupTotals.overallCount, rollupTotals.overallTotal);
                     
            // Calc the standard diviation of all values 
            rollupTotals.stdDeviation = this.computeStdDev(rollupTotals.allSkillScores, rollupTotals.overallAverage);
    
            // Calc the final bubble size
            rollupTotals.bubbleSize = rollupTotals.overallAverage / rollupTotals.stdDeviation
    
            return rollupTotals.bubbleSize;
        }
    
    /**
     * Computes a simple average
     */
    private computeAverage(count: number, total: number): number {
        if (count == 0) {
            return 0;
        }

        return total / count;
    }

    /** 
     * Computes the Standard Devision for the scores using the already computed average
     * 
     * Note: This could be written using .reduce, but keep it as follows for readability/maintainability
    */
    private computeStdDev(scores: number[], overallAverage: number) {

        let runningSum: number = 0;

        // Sum the square of the difference between each value and the average
        for(const value of scores) {
            runningSum += Math.pow(value - overallAverage, 2);
        }

        // Take that sum, divide the number of values, then take the square root
        return Math.sqrt(runningSum / scores.length);
    }

    /**
     * Determine if a category should be included - used to control when revenue & bidding questions as displayed
     */
    private includeCategory(category: EmployeeReviewCategoryEntity, revenueBiddingOptions: RevenueBillingOptions): boolean {
        if (category.EmployeeReviewCategoryId == EmployeeReviewCategories.bidding && revenueBiddingOptions.hideBiddingQuestions) {
            return false;
        }

        if (category.EmployeeReviewCategoryId == EmployeeReviewCategories.revenue && revenueBiddingOptions.hideRevenueQuestions) {
            return false;
        }

        return true;
    }

    /**
     * Determines if revenue/bidding questions should be display based on the question setup hierarchy
     */
    determineRevenueBiddingOptions(questionSetup: EmployeeReviewQuestionSetupEntity[]): RevenueBillingOptions {
        const revenueBillingOptions: RevenueBillingOptions = {
            hideRevenueQuestions: false,
            hideBiddingQuestions: false
        };

        for (const setup of questionSetup) {

            revenueBillingOptions.hideBiddingQuestions = this.getBoolValue(setup.HideBiddingQuestions, revenueBillingOptions.hideBiddingQuestions);
            revenueBillingOptions.hideRevenueQuestions = this.getBoolValue(setup.HideRevenueQuestions, revenueBillingOptions.hideRevenueQuestions);
        }

        return revenueBillingOptions;
    }

    /**
     * Determines if revenue/bidding questions should be display on the employee/supervisor reviews. (If either show the questions, then they are dispalyed)
     */
    determineRevenueBiddingOptionsByReviews(reviews: EmployeeReviewEntity[]): RevenueBillingOptions {
        const revenueBillingOptions: RevenueBillingOptions = {
            hideRevenueQuestions: false,
            hideBiddingQuestions: false
        };

        for (const review of reviews) {

            revenueBillingOptions.hideBiddingQuestions = this.getBoolValue(!review.ResponsibleForBidding, revenueBillingOptions.hideBiddingQuestions);
            revenueBillingOptions.hideRevenueQuestions = this.getBoolValue(!review.ResponsibleForRevenue, revenueBillingOptions.hideRevenueQuestions);
        }

        return revenueBillingOptions;
    }

    private getBoolValue(value: boolean, currentValue: boolean) {
        if (value === true) {
            return true;
        }
        if (value === false) {
            return false;
        }

        return currentValue;
    }
}


export interface RevenueBillingOptions {
    hideBiddingQuestions?: boolean;
    hideRevenueQuestions?: boolean;
}

// Skill Types
export interface EmployeeReviewAnalysis {
    employeeSkillAverage: SkillAverage;
    supervisorSkillAverage: SkillAverage;

    customQuestions: QuestionSummary[];
    skillQuestions: QuestionSummary[];
}

export interface QuestionSummary {
    question: EmployeeReviewQuestionEntity;

    employeeAnswer: EmployeeReviewAnswerEntity;
    employeeScore?: number;
    employeeDisplay: string;

    supervisorAnswer: EmployeeReviewAnswerEntity;
    supervisorScore?: number;
    supervisorDisplay: string;
}

export interface SkillAverage {
    softSkillAverage: number;
    hardSkillAverage: number;
    
    // Just for troubleshooting purposes
    bubbleSize?: number;
    rollupTotals?: ScoreRollupTotals;
}

interface ScoreRollupTotals {
    softSkillTotal: number;
    softSkillCount: number;
    hardSkillTotal: number;
    hardSkillCount: number;

    allSkillScores: number[];

    bubbleSize?: number;
    stdDeviation?: number;
    overallAverage?: number;
    overallTotal?: number;
    overallCount?: number;
}
